import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, Stack, Divider } from "@mui/material";
import CustomerListContainer from "../../containers/CustomerListContainer/CustomerListContainer";
import { useDispatch } from "react-redux";
import { fetchDevicesConfigs } from "../../../src/Dashboard/ducks/devicesConfigsDuck";
import config from "config";
import { DevicesConfig, sensorManagement } from "../../../src/Dashboard/actions/apiActions";
import { useSelectedSensorSite } from '../../hooks/useCloudSelectors';
import AddSensors from "./AddSensors";
import SensorManagementListItem from "./SensorManagementListItem";

// Normalize API response (PascalCase) to camelCase used by SensorCard/SensorManagementListItem
const normalizeSensor = (s) => ({
  hwId: s.HwId ?? s.hwId ?? "",
  sensorName: s.SensorName ?? s.sensorName ?? "",
  deviceType: s.DeviceType ?? s.deviceType ?? "",
  siteId: s.SiteId ?? s.siteId ?? "",
  edgeUnitId: s.EdgeUnitId ?? s.edgeUnitId ?? "",
  id: s.Id ?? s.id ?? null,
  twinState: s.TwinState ?? s.twinState ?? null,
  config: {
    alarms: (s.Config ?? s.config)?.alarms ?? (s.Config ?? s.config)?.Alarms ?? [],
    valueConversions: (s.Config ?? s.config)?.valueConversions ?? (s.Config ?? s.config)?.ValueConversions ?? [],
    valueRefinement: (s.Config ?? s.config)?.valueRefinement ?? (s.Config ?? s.config)?.ValueRefinement ?? [],
  },
});

const SensorManagement = ({ }) => {
  const theme = useTheme();
  const selectedSensorSite = useSelectedSensorSite();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  console.log("selectedSensorSite", selectedSensorSite);

  const [sensors, setSensors] = useState([]);

  const [copiedSensor, setCopiedSensor] = useState(null);

  console.log("sensors", sensors);

  // Fetch sensors using sensorManagement.getSensorsForEdgeUnit api
  const fetchSensorsForEdgeUnit = async () => {
    if (!selectedSensorSite || selectedSensorSite?.solutionType !== "lora") {
      return;
    }

    const body = {
      siteId: selectedSensorSite?.customerId,
      edgeUnitId: selectedSensorSite?.deviceId,
    };

    const response = await sensorManagement.getSensorsForEdgeUnit(config.CONFIGURATION_URL, body);
    if (response) {
      console.log("fetchSensorsForEdgeUnit response", response);
      if (response.status === 200) {
        setSensors((response.data || []).map(normalizeSensor));
      } else {
        setSensors([]); // Clear sensors if the response is not successful
        console.error("Failed to fetch sensors for edge unit:", response);
      }
    } else {
      console.error("Failed to fetch sensors for edge unit or no sensors found.");
      setSensors([]); // Clear sensors if no response or no sensors found
    }
  };

  useEffect(() => {
    if (selectedSensorSite && selectedSensorSite?.solutionType === "lora") {
      fetchSensorsForEdgeUnit();
    } else {
      setSensors([]); // Clear sensors if no valid sensor site is selected
    }
  }, [selectedSensorSite]);

  //// this gets sensor from config file, but it is not used in this component, as it does not correlate with datebase data, use fetchSensorsForEdgeUnit instead
  // const fetchSensorDevices = async () => {
  //   const payload = {
  //     customerId: selectedSensorSite?.customerId,
  //     deviceId: selectedSensorSite?.deviceId,
  //     file: selectedSensorSite?.config,
  //     isSensor: selectedSensorSite?.solutionType === "lora" ? true : false
  //   };
  //   const response = await DevicesConfig.get(config.CONFIGURATION_URL, payload);
  //   console.log("fetchSensorDevices response", response);
  //   if (response && response.deviceConfigs && response.deviceConfigs.devices) {
  //     setSensors(response.deviceConfigs.devices);
  //   } else {
  //     console.error("Failed to fetch sensor devices or no devices found.");
  //   }
  // };

  const copySensorToNewSensorCard = (sensor) => {
    // This function can be used to copy sensor data to a new sensor card
    console.log("Copying sensor data to new sensor card:", sensor);
    const newSensor = {
      ...sensor,
      hwId: "", // Clear hwId to create a new sensor
      sensorName: `${sensor.sensorName} (Copy)`,
      id: null, // Clear id to create a new sensor
    };
    delete newSensor.id; // Remove id just in case
    setCopiedSensor(newSensor);
  };

  const resetCopiedSensor = () => {
    //after new card is creted with copied data, reset copiedSensor state
    setCopiedSensor(null);
  };

  return (
    <Box
      sx={{
        flexDirection: "row",
        display: "flex",
        overflowY: "hidden",
        flex: 1,
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            backgroundColor: theme.palette.background.dark,
            overflowY: "auto",
            borderRight: `1px solid ${theme.palette.primary.main}`,
            maxWidth: { lg: "350px", xl: "370px", xxl: "400px" },
          }}
        >
          <CustomerListContainer selectedSensorSite={selectedSensorSite} sensorManagementView={true} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Box sx={{ padding: 2 }}>
          {!selectedSensorSite && (
            <Typography variant="h6" gutterBottom>
              Select a sensor site to manage sensors.
            </Typography>
          )}
          {selectedSensorSite && selectedSensorSite?.solutionType !== "lora" && (
            <Stack>
              <Typography variant="h6" gutterBottom>
                Site is not configured for sensor management.
              </Typography>
              <Typography variant="body2" gutterBottom>
                (SolutionType is not "lora")
              </Typography>
            </Stack>
          )}
          {selectedSensorSite && selectedSensorSite?.solutionType === "lora" && (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography variant="h4">{selectedSensorSite?.deviceName}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} color="primary" />
            </>
          )}
        </Box>

        <Box sx={{ overflowY: "auto", p: 2 }}>
          {selectedSensorSite && selectedSensorSite?.solutionType === "lora" && (
            <AddSensors
              selectedSensorSite={selectedSensorSite}
              fetchSensorsForEdgeUnit={fetchSensorsForEdgeUnit}
              copiedSensor={copiedSensor}
              resetCopiedSensor={resetCopiedSensor}
            />
          )}
          {sensors.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} color="primary" />
              <Box>
                <Typography variant="h5" gutterBottom>
                  Sensor List
                </Typography>

                <Stack spacing={1}>
                  {sensors.map((sensor, index) => (
                    <SensorManagementListItem
                      key={sensor.hwId}
                      sensor={sensor}
                      selectedSensorSite={selectedSensorSite}
                      fetchSensorsForEdgeUnit={fetchSensorsForEdgeUnit}
                      copySensorToNewSensorCard={copySensorToNewSensorCard}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SensorManagement;
